import ItineraryPackage from '../ItineraryPackage';
import { type PassengerCount } from '../types';
import type PriceType from '../PriceType';
import type TripSegment from './TripSegment';
import type Trip from './Trip';
import type TripPriceClass from './TripPriceClass';
import { type SelectedSuggestionServices } from './SuggestionService';
import {
    CapacityTextStatus,
    type Facilities,
    type SegmentOption,
    TripInfoComponent,
    type TripPackageDetails,
    VehicleType,
} from './types';
import type VerifyResult from '@itinerary/VerifyResult';
import { type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';
import { ReserveType } from '@utils/itinerary';

export default abstract class TripPackage<
    TTrip extends Trip = Trip,
    TPrice extends TripPriceClass = TripPriceClass,
> extends ItineraryPackage<TPrice> {
    override get optionalServiceTitle(): string {
        return `انتخاب خدمات ${this.originName} به ${this.destinationName}`;
    }

    override get freeServiceTitle(): string {
        return `انتخاب خدمات رایگان ${this.originName} به ${this.destinationName}`;
    }

    get totalDurationDays() {
        const departureStart = dayConfig
            .gregory(this.departureDateTime)
            .set('hours', 0)
            .set('minutes', 0)
            .set('seconds', 0);
        const arrivalStart = dayConfig
            .gregory(this.arrivalDateTime)
            .set('hours', 0)
            .set('minutes', 0)
            .set('seconds', 0);
        return arrivalStart.diff(departureStart, 'days');
    }

    facilities: Facilities | null = null;

    get label(): string | null {
        return null;
    }

    get hasSeatSelection() {
        return false;
    }

    get detailsMessage(): string | null {
        return null;
    }

    get hasExtraBaggage() {
        return false;
    }

    get tripPackageDetails(): TripPackageDetails[] {
        return [
            {
                title: 'اطلاعات سفر',
                component: TripInfoComponent.PackageInfo,
            },
            {
                title: this.hasExtraBaggage
                    ? 'قوانین بار و کنسلی'
                    : 'قوانین کنسلی',
                component: TripInfoComponent.CancellationTerms,
            },
        ];
    }

    extraLogoTitle: string | null = null;

    international = false;

    havePriceTrack = true;
    haveDecreasingPriceNotifier = true;

    /**
     * keep user selected suggestion services (may need to move trip)
     */
    selectedSuggestionServices: SelectedSuggestionServices[] = [];
    get defaultBuyText(): string {
        if (this.reservable) {
            return formatMoney(this.singlePayable, {
                includeUnit: true,
            });
        } else {
            return 'غیرقابل رزرو';
        }
    }

    mergedSegment = false;

    /**
     * get a title base on reserve type
     */
    get vehicleName() {
        switch (this.vehicleType) {
            case VehicleType.Plane:
                return 'پرواز';
            case VehicleType.Train:
                return 'قطار';
            case VehicleType.Bus:
                return 'اتوبوس';
            case VehicleType.Taxi:
                return 'سواری';
            default:
                return '';
        }
    }

    override get invoiceTitle() {
        return `${this.vehicleName} ${this.origin} به ${this.destination}`;
    }

    override get termsAndConditions(): string | null {
        if (this.selectedPrice) {
            return this.selectedPrice.cancellationTerms;
        } else {
            return null;
        }
    }

    abstract get serviceLabel(): string;

    get duration() {
        return this.segments.reduce((prev, { duration }) => prev + duration, 0);
    }

    override get termsAndConditionsTitle(): string {
        return `شرایط و مقررات: (${this.originName} به ${this.destinationName})`;
    }

    loadBaggage(): Promise<any> {
        return Promise.resolve();
    }

    override async createBill(
        count: PassengerCount,
        billId: number | null
    ): Promise<VerifyResult> {
        const result = await this.selectedPrice!.verify(count, billId);
        if (this.selectedSuggestionServices.length) {
            result.priceEntries[0].prices = [
                ...result.priceEntries[0].prices,
                ...this.selectedSuggestionsEntries,
            ];
            result.payablePrice +=
                this.totalSelectedSuggestionServicePriceForCheck;
        }
        return result;
    }

    get selectedSuggestionsEntries(): InvoiceEntry[] {
        return this.selectedSuggestionServices.map(s => ({
            count: s.count,
            title: s.service.title,
            discount: 0,
            price: s.service.price,
            paxType: PaxType.Adult,
        }));
    }

    get totalSelectedSuggestionServicePriceForCheck(): number {
        return this.selectedSuggestionServices.reduce(
            (total, curr) => total + curr.count * curr.service.price,
            0
        );
    }

    get tripTitle() {
        return `${this.firstTrip.origin} به ${
            this.segments[0].trips.at(-1)?.destination
        }`;
    }

    override async loadPreInvoice(count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): Promise<InvoiceEntry[]> {
        const pricePrebills = await this.selectedPrice!.loadPreInvoice(count);
        return [...pricePrebills, ...this.selectedSuggestionsEntries];
    }

    get selectedPrice(): TPrice | null {
        if (this.selectedPrices) {
            return this.selectedPrices[0];
        } else {
            return null;
        }
    }

    set selectedPrice(priceClass: TPrice | null) {
        if (priceClass) {
            this.selectedPrices = [priceClass];
        } else {
            this.selectedPrices = null;
        }
    }

    override get selectedPricesWithPriceClass():
        | [TPrice, PriceType<TPrice>][]
        | null {
        const priceType = this.prices.find(
            pt => !!pt.classes.find(p => p === this.selectedPrice)
        );
        if (priceType && this.selectedPrice) {
            return [[this.selectedPrice, priceType]];
        } else {
            return null;
        }
    }

    /**
     * Specifies an arbitrary external url to opened when tying to reserve, instead of normal reservation flow
     */
    reservationUrl: string | null = null;

    get origin(): string {
        return this.segments[0].trips[0].origin;
    }

    get originName(): string {
        return this.origin.replace('(همه فرودگاه‌ها)', '');
    }

    get destinationName(): string {
        return this.destination.replace('(همه فرودگاه‌ها)', '');
    }

    override get raiseTitle(): string {
        const departureDate = this.departureDateTime;
        if (!this.origin || !this.destination || !departureDate) {
            return '';
        }
        const names = {
            [ReserveType.Flight]: 'هواپیما',
            [ReserveType.Train]: 'قطار',
            [ReserveType.Bus]: 'اتوبوس',
            [ReserveType.Taxi]: 'سواری',
            [ReserveType.Hotel]: 'هتل',
            [ReserveType.Route]: 'سفر',
        }; // temp
        const viewTitle = `بلیط ${names[this.reserveType]} ${
            this.originName
        } به ${this.destinationName}`;

        return `گزینه های ${viewTitle}`;
    }

    get destination(): string {
        return this.segments[0]?.lastTrip?.destination || '';
    }

    get originCode(): string {
        return this.segments[0]?.firstTrip?.originCode || '';
    }

    get destinationCode(): string {
        return this.segments[0]?.lastTrip?.destinationCode || '';
    }

    get departureTime24(): string {
        return this.segments[0].trips[0].departureTime;
    }

    get arrivalTime24(): string {
        return this.segments[0].trips[this.segments[0].trips.length - 1]
            .arrivalTime;
    }

    get serviceId(): number {
        return this.segments[0].trips[0].serviceId;
    }

    get capacityStatus(): CapacityTextStatus {
        if (!this.defaultPrice) {
            return CapacityTextStatus.normal;
        }
        return this.defaultPrice.capacity < 9
            ? CapacityTextStatus.low
            : CapacityTextStatus.normal;
    }

    get showCapacityOnCard(): boolean {
        return true;
    }

    get serviceNumber(): string {
        return this.segments[0].trips[0].serviceNumber;
    }

    get firstTrip(): Trip {
        return this.segments?.[0]?.trips[0];
    }

    get lastTrip(): Trip {
        return this.segments?.[this.segments.length - 1]?.trips[
            this.segments[this.segments.length - 1].trips.length - 1
        ];
    }

    get departureDateTime(): Date {
        return this.firstTrip?.departureDateTime;
    }

    get arrivalDateTime(): Date {
        return this.lastTrip?.arrivalDateTime;
    }

    get departureTime(): string {
        return this.firstTrip?.departureTime;
    }

    get arrivalTime(): string {
        return this.lastTrip?.arrivalTime;
    }

    get capacity(): number {
        return this.selectedPrice ? this.selectedPrice.capacity : 0;
    }

    get capacityText(): string {
        return this.selectedPrice ? this.selectedPrice.capacityText : '';
    }

    get priceDescription(): string | null {
        return this.selectedPrice ? this.selectedPrice.priceDescription : null;
    }

    get capacityTextReplacement(): string | null {
        return this.selectedPrice
            ? this.selectedPrice.capacityTextReplacement
            : null;
    }

    abstract get segments(): TripSegment<TTrip>[];

    override get buyText(): string {
        if (this.capacity === 0) {
            return 'تکمیل ظرفیت';
        } else if (this.capacity < this.totalCount) {
            return 'ظرفیت ناکافی';
        } else {
            return this.defaultBuyText;
        }
    }

    override get reservable(): boolean {
        return this.capacity >= this.totalCount;
    }

    get showReserveButton() {
        return this.reservable || this.hasAutoReserve;
    }

    override get isFull() {
        return this.capacity < this.totalCount;
    }

    override get buyTooltip(): string {
        if (this.capacity < this.totalCount) {
            return `ظرفیت این سرویس در حال حاضر تکمیل بوده و امکان رزرو ندارد. چنانچه ظرفیتی آزاد شود به صورت آنلاین نمایش داده خواهد شد.`;
        } else {
            return '';
        }
    }

    override get singlePayable(): number {
        return this.defaultPrice?.singlePayable || 0;
    }

    override get singlePrice(): number {
        return this.defaultPrice?.singlePrice || 0;
    }

    override get totalPayableForCheck(): number | null {
        if (this.selectedPrice?.totalPayableForCheck) {
            return (
                this.selectedPrice.totalPayableForCheck +
                this.totalSelectedSuggestionServicePriceForCheck
            );
        } else {
            return null;
        }
    }

    override get totalPriceForCheck(): number | null {
        if (this.selectedPrice?.totalPriceForCheck) {
            return (
                this.selectedPrice.totalPriceForCheck +
                this.totalSelectedSuggestionServicePriceForCheck
            );
        } else {
            return null;
        }
    }

    override checkNeedsRaiseClass(count: PassengerCount): boolean {
        return (
            super.checkNeedsRaiseClass(count) ||
            this.segments
                .reduce(
                    (trips, segment) => [...trips, ...segment.trips],
                    [] as TTrip[]
                )
                .some(t => t.suggestionServices.length)
        );
    }

    get canTrackPackageCapacity(): boolean {
        return !this.reservable;
    }

    get canPriceAutoReserve() {
        const { isMrbilit } = useWhiteLabel();
        return isMrbilit.value && this.reservable && this.hasAutoReserve;
    }

    get loadableTermsUrl(): string | null {
        return this.selectedPrice?.loadableTermsUrl || null;
    }

    get vehicleType(): VehicleType {
        return this.firstTrip.vehicleType;
    }

    get pointOption(): SegmentOption | null {
        return null;
    }

    abstract override get callToReserve(): boolean;
    get allGroupedTrips(): Trip[] {
        return this.segments.reduce(
            (grouped, segment) => [...grouped, ...segment.trips],
            [] as Trip[]
        );
    }
}
